import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { dashboardGuard } from '@core/guards/dashboard.guard';
import { roleGuard } from '@core/guards/role.guard';

/**
 * Routing-Baum für die GoldApp-Shell.
 *
 * - `/auth/*` – Login, Self-Reg, Passwort-Reset (gated gegen eingeloggte User).
 * - `/admin/*` – Admin-Backoffice (Sprint 3, staff-only), eigene Shell
 *   mit Sidenav.
 * - `/` – Customer-Shell mit TopNav, default-redirect auf `/depot`.
 *
 * Die Admin-Route steht vor der `''`-Customer-Route, damit `/admin/...`
 * nicht von der Customer-`**`-Wildcard aufgefressen wird.
 *
 * **V4 (Sprint 5c S5c-4):** `/auth/verify-2fa` entfernt (kein 2FA mehr).
 * `/auth/register` neu — öffentliche Self-Registration (ADR-005 D2).
 * `/auth/activate` neu (Sprint 5c S5c-6) — Token + Email aus dem
 * Mail-Link, Auto-Submit, zweistufiger Pfad bei Admin-Stub-Konten.
 */
export const routes: Routes = [
  {
    path: 'auth',
    canActivate: [authGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('@features/auth/login/login.component').then(
            (m) => m.LoginComponent,
          ),
      },
      {
        path: 'register',
        loadComponent: () =>
          import('@features/auth/register/register.component').then(
            (m) => m.RegisterComponent,
          ),
      },
      {
        path: 'activate',
        loadComponent: () =>
          import('@features/auth/activate/activate.component').then(
            (m) => m.ActivateComponent,
          ),
      },
      {
        path: 'password-forgotten',
        loadComponent: () =>
          import(
            '@features/auth/password-forgotten/password-forgotten.component'
          ).then((m) => m.PasswordForgottenComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import(
            '@features/auth/reset-password/reset-password.component'
          ).then((m) => m.ResetPasswordComponent),
      },
      { path: '**', redirectTo: 'login' },
    ],
  },
  {
    path: 'admin',
    canActivate: [dashboardGuard, roleGuard],
    data: { role: 'staff' },
    loadComponent: () =>
      import('./features/admin/shell/admin-shell.component').then(
        (m) => m.AdminShellComponent,
      ),
    children: [
      { path: '', redirectTo: 'kunden', pathMatch: 'full' },
      {
        path: 'kunden',
        loadComponent: () =>
          import('./features/admin/customers/admin-customers.component').then(
            (m) => m.AdminCustomersComponent,
          ),
      },
      // Listen/Detail-Split: Zeilenklick in der Kundentabelle führt auf
      // den eigenständigen Detail-Bildschirm mit Zurück-Pfeil.
      {
        path: 'kunden/:id',
        loadComponent: () =>
          import(
            './features/admin/customers/admin-customer-detail-page.component'
          ).then((m) => m.AdminCustomerDetailPageComponent),
      },
      // Sprint 8 (Sell-Workflow-UI-Split): `/admin/auftraege` ist jetzt
      // in `kauf` + `verkauf` aufgespalten. Beide Routes laden die
      // gleiche Listen-Component, der TX-Type kommt aus `data.type`.
      // Default-Redirect von `/admin/auftraege` → `/admin/auftraege/kauf`.
      { path: 'auftraege', redirectTo: 'auftraege/kauf', pathMatch: 'full' },
      {
        path: 'auftraege/kauf',
        data: { type: 'buy' as const },
        loadComponent: () =>
          import(
            './features/admin/transactions/admin-transactions.component'
          ).then((m) => m.AdminTransactionsComponent),
      },
      {
        path: 'auftraege/verkauf',
        data: { type: 'sell' as const },
        loadComponent: () =>
          import(
            './features/admin/transactions/admin-transactions.component'
          ).then((m) => m.AdminTransactionsComponent),
      },
      {
        path: 'auftraege/:id',
        loadComponent: () =>
          import(
            './features/admin/transactions/admin-transaction-review.component'
          ).then((m) => m.AdminTransactionReviewComponent),
      },
      {
        path: 'dokumente',
        loadComponent: () =>
          import('./features/admin/documents/admin-documents.component').then(
            (m) => m.AdminDocumentsComponent,
          ),
      },
      {
        path: 'lagergebuehren',
        loadComponent: () =>
          import(
            './features/admin/storage-fees/storage-fees-list.component'
          ).then((m) => m.StorageFeesListComponent),
      },
      {
        path: 'lagergebuehren/einstellungen',
        loadComponent: () =>
          import(
            './features/admin/storage-fees/storage-fee-settings.component'
          ).then((m) => m.StorageFeeSettingsComponent),
      },
      // P18: globale Agio-Sätze pro Metall.
      {
        path: 'agio',
        loadComponent: () =>
          import('./features/admin/agio/agio-settings.component').then(
            (m) => m.AgioSettingsComponent,
          ),
      },
      // Info-Mail (User-Feedback 2026-08): Vorlage + Anhänge + Kunden-PDFs.
      {
        path: 'info-mail',
        loadComponent: () =>
          import('./features/admin/info-mail/info-mail-settings.component').then(
            (m) => m.InfoMailSettingsComponent,
          ),
      },
      { path: '**', redirectTo: 'kunden' },
    ],
  },
  // Sprint 9 Refinement (ADR-011): öffentliche, token-gesicherte In-App-
  // Signierseite (E-Mail / QR, kein Login). MUSS vor dem Customer-Block
  // stehen, dessen `**` sonst auf `depot` (auth-gated) umleitet.
  {
    path: 'sign/:token',
    loadComponent: () =>
      import(
        './features/customer/purchase-order/public/public-sign-page.component'
      ).then((m) => m.PublicSignPageComponent),
  },
  {
    path: '',
    canActivate: [dashboardGuard, roleGuard],
    data: { role: 'customer' },
    loadComponent: () =>
      import('@features/customer/shell/main.component').then(
        (m) => m.MainComponent,
      ),
    children: [
      { path: '', redirectTo: 'depot', pathMatch: 'full' },
      {
        path: 'depot',
        loadComponent: () =>
          import('./features/customer/depot/depot.component').then((m) => m.DepotComponent),
      },
      {
        path: 'transaktionen',
        loadComponent: () =>
          import('./features/customer/transactions/transactions.component').then(
            (m) => m.TransactionsComponent,
          ),
      },
      {
        path: 'dokumente',
        loadComponent: () =>
          import('./features/customer/documents/documents.component').then(
            (m) => m.DocumentsComponent,
          ),
      },
      // Sprint 9 (ADR-011): Kaufauftrag-Seite (Ident-Wahl, Ausweis-Upload,
      // Signatur). Hinter Login — der Mail-Link führt via returnUrl hierher.
      {
        path: 'auftrag/:id',
        loadComponent: () =>
          import('./features/customer/purchase-order/purchase-order-page.component').then(
            (m) => m.PurchaseOrderPageComponent,
          ),
      },
      // Markt-Tab entfernt (User-Feedback 2026-08); stattdessen der
      // Musterkaufauftrag-Tab mit Muster-PDF + AGB zum Download.
      {
        path: 'musterkaufauftrag',
        loadComponent: () =>
          import('./features/customer/sample-order/sample-order.component').then(
            (m) => m.SampleOrderComponent,
          ),
      },
      {
        path: 'profil',
        loadComponent: () =>
          import('./features/customer/profile/profile.component').then((m) => m.ProfileComponent),
      },
      { path: '**', redirectTo: 'depot' },
    ],
  },
  { path: '**', redirectTo: 'auth' },
];
